import { useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Paper, Stack, Table, TableBody, TableCell,
  TableFooter, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { reportsApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

function defaultFrom(): string {
  const d = new Date(); d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}
function defaultTo(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * UC-M1-REP-04 — Daily sales report. One row per day in the range with bill count,
 * subtotal, tax, and net amount summed from Posted sales invoices (OP + IP).
 */
export function DailySalesReportPage() {
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);

  const enabled = !!fromDate && !!toDate;
  const query = useQuery({
    queryKey: ["report-daily-sales", fromDate, toDate],
    queryFn: () => reportsApi.dailySales(fromDate, toDate),
    enabled,
  });

  const rows = query.data ?? [];
  const totals = rows.reduce((acc, r) => ({
    billCount: acc.billCount + r.billCount,
    subTotal: acc.subTotal + r.subTotal,
    tax: acc.tax + r.tax,
    net: acc.net + r.netAmount,
  }), { billCount: 0, subTotal: 0, tax: 0, net: 0 });

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Daily sales</Typography>
        <Button onClick={() => navigate("/reports")}>Back to reports</Button>
      </Stack>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField type="date" label="From *" size="small" InputLabelProps={{ shrink: true }}
            value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          <TextField type="date" label="To *" size="small" InputLabelProps={{ shrink: true }}
            value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </Stack>
        {!enabled && <Alert severity="info">Pick a From and To date.</Alert>}
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {enabled && (query.isLoading
          ? <CircularProgress />
          : rows.length === 0
            ? <Alert severity="info">No posted sales invoices in the selected range.</Alert>
            : (
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Bills</TableCell>
                  <TableCell align="right">Subtotal</TableCell>
                  <TableCell align="right">Tax</TableCell>
                  <TableCell align="right">Net amount</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {rows.map(r => (
                    <TableRow key={r.day} hover>
                      <TableCell>{new Date(r.day).toLocaleDateString()}</TableCell>
                      <TableCell align="right">{r.billCount}</TableCell>
                      <TableCell align="right">{inr.format(r.subTotal)}</TableCell>
                      <TableCell align="right">{inr.format(r.tax)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500 }}>{inr.format(r.netAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Totals</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{totals.billCount}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{inr.format(totals.subTotal)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{inr.format(totals.tax)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{inr.format(totals.net)}</TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            ))}
      </Paper>
    </Box>
  );
}
